import { API_BASE } from "./config/api";
import React, { useEffect, useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Spin, Grid } from "antd";
import { useNavigate } from "react-router-dom";
import {
  getDistributorToken,
  handleDistributorAuthFailure,
  redirectDistributorToLogin,
} from "./config/auth";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [costAmount, setCostAmount] = useState(0);
  const [currentValue, setCurrentValue] = useState(0);
  const [previousDayChangeAmount, setPreviousDayChangeAmount] = useState(null);
  const [previousDayChangePercentage, setPreviousDayChangePercentage] = useState(null);
  const [schemeSummary, setSchemeSummary] = useState([]);
  const [amcSummary, setAmcSummary] = useState([]);
  // XIRR and Absolute Return (existing)
  const [xirr, setXirr] = useState(null);
  const [absolute, setAbsolute] = useState(null);
  // NEW: per-scheme-type XIRR
  const [xirrEquity, setXirrEquity] = useState(null);
  const [xirrDebt, setXirrDebt] = useState(null);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.lg; // < lg

  const arn = localStorage.getItem("arn_number") || "";

  const formatIndianCurrency = (amount) => {
    amount = Number(amount || 0);
    if (amount < 100000) return `₹${amount.toLocaleString("en-IN")}`;
    if (amount < 10000000) return `₹${(amount / 100000).toFixed(2)} Lakh`;
    return `₹${(amount / 10000000).toFixed(2)} Cr`;
  };

  const formatPercent = (val) => {
    if (val === null || val === undefined || isNaN(Number(val))) return "—";
    return `${Number(val).toFixed(2)}%`;
  };

  useEffect(() => {
    const token = getDistributorToken();
    if (!arn || !token) {
      redirectDistributorToLogin();
      return;
    }

    fetch(`${API_BASE}/get_total.php`, {
      headers: { "X-KG-Distributor-Authorization": `Bearer ${token}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (handleDistributorAuthFailure(res, data)) return null;
        if (!res.ok) throw new Error(data?.message || data?.error || "Unable to load dashboard.");
        return data;
      })
      .then((data) => {
        if (!data) return;
        setCostAmount(Number(data?.remaining_cost_amount || 0));
        setCurrentValue(Number(data?.remaining_current_value || 0));
        setPreviousDayChangeAmount(data?.previous_day_change_amount == null ? null : Number(data.previous_day_change_amount));
        setPreviousDayChangePercentage(data?.previous_day_change_percentage == null ? null : Number(data.previous_day_change_percentage));
        setSchemeSummary(data?.scheme_type_summary || []);
        setXirr(data?.xirr ?? null);
        setAbsolute(data?.absolute ?? null);
        // NEW: per-scheme-type XIRR from API (kept for fallback)
        setXirrEquity(data?.xirr_equity ?? null);
        setXirrDebt(data?.xirr_debt ?? null);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    fetch(`${API_BASE}/get_amc_wise_data.php`, {
      headers: { "X-KG-Distributor-Authorization": `Bearer ${token}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (handleDistributorAuthFailure(res, data)) return null;
        if (!res.ok) throw new Error(data?.message || data?.error || "Unable to load AMC data.");
        return data;
      })
      .then((data) => {
        if (data) setAmcSummary(Array.isArray(data?.data) ? data.data : []);
      })
      .catch(() => setAmcSummary([]));
  }, [arn, navigate]);

  const gainLoss = currentValue - costAmount;
  const posColor = "#16a34a";
  const negColor = "#dc2626";
  const profit = Math.max(gainLoss, 0);

  const baseDonutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "70%",
      plugins: {
        legend: { position: "bottom" },
        tooltip: {
          callbacks: {
            label: (t) => `${t.label || ""}: ${formatIndianCurrency(t.raw || 0)}`,
          },
        },
      },
    }),
    []
  );

  const totalChartData = useMemo(
    () => ({
      labels: ["Cost Amount", "Gain"],
      datasets: [
        {
          data: [costAmount, profit],
          backgroundColor: ["#36A2EB", "#FFCE56"],
          borderWidth: 1,
        },
      ],
    }),
    [costAmount, profit]
  );

  const amcAllocation = useMemo(() => {
    const clean = (amcSummary || []).map(a => ({
      name: a.amc_name || "Unknown AMC",
      value: Number(a.current_value || 0)
    })).filter(a => a.value > 0).sort((a,b) => b.value-a.value);
    return clean;
  }, [amcSummary]);

  const amcTotal = useMemo(() => amcAllocation.reduce((x,a)=>x+a.value,0), [amcAllocation]);
  const amcColors = ["#1677ff","#22c55e","#ff8a00","#7c3aed","#06b6d4","#ec4899","#84cc16","#4f46e5","#d946ef","#a855f7","#14b8a6","#f97316","#0ea5e9","#e11d48","#65a30d","#8b5cf6","#0284c7","#db2777","#059669","#ca8a04"];

  const amcChartData = useMemo(() => ({
    labels: amcAllocation.map(a=>a.name),
    datasets: [{
      data: amcAllocation.map(a=>a.value),
      backgroundColor: amcAllocation.map((_, i) => amcColors[i % amcColors.length]),
      borderColor: "#ffffff",
      borderWidth: 4,
      hoverOffset: 5
    }]
  }), [amcAllocation]);

  const amcChartOptions = useMemo(() => ({
    responsive:true, maintainAspectRatio:false, cutout:"55%",
    plugins:{ legend:{display:false}, tooltip:{callbacks:{label:(t)=>{
      const v=Number(t.raw||0), p=amcTotal ? v/amcTotal*100 : 0;
      return `${t.label}: ${formatIndianCurrency(v)} (${p.toFixed(2)}%)`;
    }}}}
  }), [amcTotal]);

  if (loading) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 12 : 22, background: "linear-gradient(135deg,#eef4ff 0%,#f7f0ff 34%,#fff3f1 66%,#effcf4 100%)", minHeight: "100vh" }}>
      <div style={{ maxWidth: 1420, margin: "0 auto" }}>
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ margin:0, fontSize:isMobile?24:30, fontWeight:700, color:"#111827" }}>Welcome back!</h1>
          <div style={{ color:"#64748b", marginTop:4 }}>Here’s what’s happening with your business today.</div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(6,minmax(0,1fr))",gap:12,marginBottom:18}}>
          {[
            ["Current Value",formatIndianCurrency(currentValue),"#1665d8","linear-gradient(135deg,#eef6ff,#ffffff)"],
            ["Previous Day Change",previousDayChangeAmount===null?"—":`${previousDayChangeAmount>=0?"+":""}${formatIndianCurrency(previousDayChangeAmount)}${previousDayChangePercentage===null?"":` (${previousDayChangePercentage>=0?"+":""}${formatPercent(previousDayChangePercentage)})`}`,previousDayChangeAmount===null?"#64748b":previousDayChangeAmount>=0?posColor:negColor,"linear-gradient(135deg,#f3f8ff,#ffffff)"],
            ["Invested Amount",formatIndianCurrency(costAmount),"#6d4bc3","linear-gradient(135deg,#f5efff,#ffffff)"],
            ["Gain / Loss",formatIndianCurrency(gainLoss),gainLoss>=0?posColor:negColor,"linear-gradient(135deg,#effbf3,#ffffff)"],
            ["Overall Return (XIRR)",formatPercent(xirr),Number(xirr)>=0?posColor:negColor,"linear-gradient(135deg,#fff5e9,#ffffff)"],
            ["Absolute Return",formatPercent(absolute),Number(absolute)>=0?posColor:negColor,"linear-gradient(135deg,#fff0f6,#ffffff)"],
          ].map(([l,v,c,bg])=><div key={l} style={{background:bg,border:"1px solid rgba(255,255,255,.95)",borderRadius:16,padding:isMobile?"14px 12px":"19px",boxShadow:"0 8px 22px rgba(37,99,235,.08)"}}>
            <div style={{fontSize:13,color:"#526078",marginBottom:9,fontWeight:400}}>{l}</div>
            <div style={{fontSize:isMobile?18:23,fontWeight:600,color:c}}>{v}</div>
          </div>)}
        </div>

        <div style={{background:"rgba(255,255,255,.94)",border:"1px solid rgba(255,255,255,.98)",borderRadius:20,padding:isMobile?14:24,boxShadow:"0 14px 38px rgba(37,99,235,.10)",backdropFilter:"blur(10px)"}}>
          <h2 style={{margin:"0 0 3px",fontSize:18,fontWeight:800}}>Current Value by AMC</h2>
          <div style={{color:"#64748b",fontSize:13,marginBottom:14}}>Current portfolio distribution</div>

          {amcAllocation.length ? <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"minmax(360px,46%) 1fr",gap:isMobile?18:34,alignItems:"center"}}>
            <div style={{height:isMobile?320:390,position:"relative"}}>
              <Doughnut data={amcChartData} options={amcChartOptions}/>
              <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none",textAlign:"center"}}>
                <div>
                  <div style={{fontSize:13,color:"#64748b"}}>Current Value</div>
                  <div style={{fontSize:isMobile?25:31,fontWeight:700}}>{formatIndianCurrency(currentValue)}</div>
                  {previousDayChangeAmount !== null && (
                    <div style={{
                      display:"inline-block",
                      marginTop:7,
                      padding:isMobile?"5px 9px":"6px 12px",
                      borderRadius:8,
                      background:previousDayChangeAmount>0?"#079669":previousDayChangeAmount<0?"#d92d20":"#64748b",
                      color:"#fff",
                      fontWeight:800,
                      fontSize:isMobile?12:14,
                      lineHeight:1.25,
                      boxShadow:"0 5px 12px rgba(15,23,42,.12)"
                    }}>
                      {previousDayChangeAmount>=0?"+":""}{formatIndianCurrency(previousDayChangeAmount)}
                      {previousDayChangePercentage===null?"":` (${previousDayChangePercentage>=0?"+":""}${formatPercent(previousDayChangePercentage)})`}
                    </div>
                  )}
                  <div style={{fontSize:13,color:"#64748b",marginTop:5}}>Total</div>
                </div>
              </div>
            </div>
            <div style={{
              maxHeight: isMobile ? 330 : 390,
              overflowY: "auto",
              paddingRight: 8,
              border: "1px solid #e7ecf3",
              borderRadius: 14,
              background: "#fbfcff"
            }}>
              <div style={{
                position:"sticky", top:0, zIndex:2, display:"grid",
                gridTemplateColumns:"18px minmax(150px,1fr) auto auto",
                gap:10, padding:"10px 8px", background:"#f4f7ff",
                borderBottom:"1px solid #e3e8f2", fontSize:12,
                fontWeight:600, color:"#475569"
              }}>
                <span></span><span>AMC Name</span><span>Current Value</span><span>Allocation</span>
              </div>
              {amcAllocation.map((a,i)=>{const p=amcTotal?a.value/amcTotal*100:0;return <div key={a.name} style={{display:"grid",gridTemplateColumns:"18px minmax(150px,1fr) auto auto",gap:10,alignItems:"center",padding:"11px 3px",borderBottom:"1px solid #edf1f6"}}>
                <span style={{width:11,height:11,borderRadius:"50%",background:amcColors[i%amcColors.length]}}/>
                <div style={{fontWeight:500}}>{a.name}</div><div style={{color:"#475569",fontWeight:500}}>{formatIndianCurrency(a.value)}</div><div style={{minWidth:62,textAlign:"right",fontWeight:600}}>{p.toFixed(2)}%</div>
              </div>})}
            </div>
          </div>:<div style={{padding:40,textAlign:"center",color:"#64748b"}}>No AMC allocation data available</div>}


        </div>
      </div>
    </div>
  );
}
